import { useApiQuery } from './useApiQuery';

export interface ActorLocation {
  id: string;
  shift: {
    id: string;
    actor: {
      id: number;
      email: string;
      fullName: string;
      role: string;
      partner: any;
      enabled: boolean;
      createdAt: string;
      updatedAt: string;
      lastLoginIp: string;
      lastLoginLocation: string;
      lastLoginAt: string;
    };
    partner: {
      id: number;
      name: string;
      orgType: string;
      partnerIdentifier: string;
      legalName: string;
      contact: string;
      website: string;
      createdAt: string;
      updatedAt: string;
      enabledModules: string[];
    };
    status: string;
    startedAt: string;
    endedAt: string | null;
    lastHeartbeatAt: string;
    startLatitude: number;
    startLongitude: number;
    endLatitude: number | null;
    endLongitude: number | null;
    endReason: string | null;
    lastPausedAt: string | null;
    numberOfPauses: number | null;
    createdAt: string;
    updatedAt: string;
  };
  actor: {
    id: number;
    email: string;
    fullName: string;
    role: string;
    partner: any;
    enabled: boolean;
    createdAt: string;
    updatedAt: string;
    lastLoginIp: string;
    lastLoginLocation: string;
    lastLoginAt: string;
  };
  latitude: number;
  longitude: number;
  accuracyMeters: number;
  locationLabel: string;
  recordedAt: string;
  locationSource: string;
  createdAt: string;
}

interface ActorLocationsResponse {
  success: boolean;
  message: string;
  data: {
    content: ActorLocation[];
    pageNumber: number;
    pageSize: number;
    totalElements: number;
    totalPages: number;
    first: boolean;
    last: boolean;
  };
}

export function useActorLocations() {
  return useApiQuery<ActorLocationsResponse>({
    queryKey: ['actor-locations'],
    request: {
      url: '/partner/shifts/actors/locations',
      method: 'GET',
    },
  });
}

// Helper function to process actor location data for map display
export function processActorLocationData(locations: ActorLocation[]) {
  if (!locations.length) return [];

  // Sort locations by recorded time
  const sortedLocations = [...locations].sort((a, b) => 
    new Date(a.recordedAt).getTime() - new Date(b.recordedAt).getTime()
  );

  // Group by shift for better organization
  const shiftsMap = new Map<string, ActorLocation[]>();
  sortedLocations.forEach(location => {
    const shiftId = location.shift.id;
    if (!shiftsMap.has(shiftId)) {
      shiftsMap.set(shiftId, []);
    }
    shiftsMap.get(shiftId)!.push(location);
  });

  // Convert to format expected by Leaflet with enhanced activity detection
  return sortedLocations.map((location, index) => {
    const shiftLocations = shiftsMap.get(location.shift.id) || [];
    const locationIndexInShift = shiftLocations.findIndex(loc => loc.id === location.id);
    const isShiftStart = locationIndexInShift === 0;
    const isShiftEnd = locationIndexInShift === shiftLocations.length - 1 && location.shift.status === 'ENDED';

    return {
      id: location.id,
      latitude: location.latitude,
      longitude: location.longitude,
      placeName: location.locationLabel || `Location ${index + 1}`,
      timestamp: location.recordedAt,
      activity: getActivityFromActorLocation(location, isShiftStart, isShiftEnd),
      registrations: 0, // This would need to be calculated from engagement data if needed
      accuracyMeters: location.accuracyMeters,
      locationSource: location.locationSource,
      shiftId: location.shift.id,
      shiftStatus: location.shift.status,
      actorName: location.actor.fullName,
    };
  });
}

// Helper function to determine activity type based on actor location data
function getActivityFromActorLocation(location: ActorLocation, isShiftStart: boolean, isShiftEnd: boolean): string {
  if (isShiftStart) return 'Shift Start';
  if (isShiftEnd) return 'Shift End';
  
  // You could add more sophisticated logic here based on time gaps, accuracy, etc.
  if (location.locationLabel === 'Current Location') {
    return 'Canvassing';
  }
  
  return 'Canvassing';
}

// Helper function to calculate map bounds for actor locations
export function calculateActorMapBounds(locations: ActorLocation[]) {
  if (!locations.length) return null;
  
  const lats = locations.map(loc => loc.latitude);
  const lngs = locations.map(loc => loc.longitude);
  
  const minLat = Math.min(...lats);
  const maxLat = Math.max(...lats);
  const minLng = Math.min(...lngs);
  const maxLng = Math.max(...lngs);
  
  return {
    minLat,
    maxLat,
    minLng,
    maxLng,
    center: [(minLat + maxLat) / 2, (minLng + maxLng) / 2] as [number, number],
  };
}
